// Shared pieces for Financial statements and the Sales book: the four money buckets, a statement table whose columns add
// across to a total, count-up numbers, KPI tiles, column charts and a client-side CSV export.
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { reduced } from '../../lib/motion'
import { n } from '../format'
import '../finance.css'

export const BUCKETS = ['shop', 'cash', 'bank', 'crypto', 'credit'] as const
export type Bucket = typeof BUCKETS[number]
export type BMap = Record<Bucket, number>
export const BUCKET_LABEL: Record<Bucket, string> = { shop: 'Shopify', cash: 'Cash', bank: 'Bank', crypto: 'Crypto', credit: 'Trade-in credit' }
export const BUCKET_SUB: Record<Bucket, string> = { shop: 'card, POS and web', cash: '', bank: 'Zelle, wire, PayPal', crypto: '', credit: 'store credit' }
export const BUCKET_COLOR: Record<Bucket, string> = { shop: '#0b0b0c', cash: '#545458', bank: '#8e8e93', crypto: '#bdbdc2', credit: '#dcdce0' }
export const METHOD_LABEL: Record<string, string> = { card: 'Card', cash: 'Cash', zelle: 'Zelle', paypal: 'PayPal', wire: 'Wire', check: 'Check', crypto: 'Crypto', trade_credit: 'Trade-in credit' }

/** $1,234.56 with a real minus sign; `whole` drops the cents. */
export function m(v: unknown, whole = false) {
  const x = n(v)
  const s = Math.abs(x).toLocaleString('en-US', whole ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (x < -0.004 ? '−$' : '$') + s
}
export const m0 = (v: unknown) => m(v, true)
export const pctS = (v: number) => Math.round(v) + '%'
const ease = (k: number) => 1 - Math.pow(1 - k, 3)

/** A number that counts up from 0 the first time it shows and rolls to each new value after that. */
export function Num({ value, format = m, ms = 800 }: { value: number; format?: (v: number) => string; ms?: number }) {
  const [shown, setShown] = useState(reduced() ? value : 0)
  const from = useRef(reduced() ? value : 0)
  useEffect(() => {
    if (reduced()) { setShown(value); from.current = value; return }
    const start = from.current, t0 = performance.now()
    let raf = 0
    const tick = (now: number) => { const k = Math.min(1, (now - t0) / ms); setShown(start + (value - start) * ease(k)); if (k < 1) raf = requestAnimationFrame(tick); else from.current = value }
    raf = requestAnimationFrame(tick)
    return () => { cancelAnimationFrame(raf); from.current = value }
  }, [value, ms])
  return <>{format(shown)}</>
}

export function Kpi({ label, value, sub, tone, delta, cmp, i = 0 }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'good'; delta?: { text: string; dir: 'up' | 'dn'; good: boolean } | null; cmp?: ReactNode; i?: number }) {
  return (
    <div className="fx-kpi fx-in" style={{ ['--d' as string]: `${i * 70}ms` }}>
      <div className="fx-kpi__top"><span>{label}</span>{delta && <span className={'fx-delta ' + (delta.good ? 'fx-delta--up' : 'fx-delta--dn')}>{delta.dir === 'up' ? '▲' : '▼'} {delta.text}</span>}</div>
      <span className={'fx-kpi__v' + (tone === 'good' ? ' fx-good' : '')}>{value}</span>
      {sub && <span className="fx-kpi__s">{sub}</span>}
      {delta && cmp && <span className="fx-kpi__cmp">{cmp}</span>}
    </div>
  )
}

export function Chev() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
}

export function Chips<T extends string>({ value, options, onChange, label }: { value: T; options: { key: T; label: ReactNode; count?: ReactNode }[]; onChange: (k: T) => void; label: string }) {
  return (
    <div className="fx-chips" role="group" aria-label={label}>
      {options.map((o) => <button key={o.key} type="button" aria-pressed={value === o.key} onClick={() => onChange(o.key)}>{o.label}{o.count != null && <small>{o.count}</small>}</button>)}
    </div>
  )
}

// ---- statements ------------------------------------------------------------------------------------------------------------
export type Cell = number | string | null
export interface StLine {
  kind: 'sec' | 'row' | 'tot' | 'gp' | 'net' | 'pct' | 'note'
  label: string; term?: string; note?: ReactNode; id?: string
  values?: Cell[]; total?: Cell
  kids?: { label: string; values: Cell[]; total: Cell }[]
}
export interface StHead { label: ReactNode; sub?: ReactNode; swatch?: string }

function cell(v: Cell, kind: StLine['kind'], strong = false): ReactNode {
  if (v == null) return <span className="fx-dash">—</span>
  if (typeof v === 'string') return <span className="fx-soft">{v}</span>
  if (kind === 'pct') return <span className="fx-pct">{pctS(v)}</span>
  if (Math.abs(v) < 0.005) return <span className="fx-dash">—</span>
  return strong ? <b><Num value={v} /></b> : <Num value={v} />
}

/** A statement: sections, lines and totals; the value columns add across to the total column. */
export function Statement({ heads, total, lines, minWidth = 720, first = 'Line' }: { heads: StHead[]; total: StHead; lines: StLine[]; minWidth?: number; first?: string }) {
  const [open, setOpen] = useState<Set<string>>(new Set())
  const cols = `minmax(0,1fr) ${heads.map(() => '140px').join(' ')} 150px`
  const style = { gridTemplateColumns: cols }
  return (
    <div className="fx-st"><div className="fx-st__in" style={{ minWidth }}>
      <div className="fx-tr fx-th" style={style}>
        <span>{first}</span>
        {heads.map((h, i) => <span key={i} className="r">{h.swatch && <i className="fx-sw" style={{ background: h.swatch }} />}{h.label}{h.sub && <small>{h.sub}</small>}</span>)}
        <span className="r">{total.label}{total.sub && <small>{total.sub}</small>}</span>
      </div>
      {lines.map((l, i) => {
        if (l.kind === 'sec') return <div key={i} className="fx-sec">{l.label}</div>
        if (l.kind === 'note') return <div key={i} className="fx-bnote">{l.note ?? l.label}</div>
        const cls = { row: '', tot: ' fx-tot', gp: ' fx-gp', net: ' fx-net', pct: '' }[l.kind]
        const isOpen = !!l.id && open.has(l.id)
        const label = l.kids?.length && l.id
          ? <button type="button" className={'fx-tog' + (isOpen ? ' is-open' : '')} aria-expanded={isOpen} onClick={() => setOpen((s) => { const x = new Set(s); if (x.has(l.id!)) x.delete(l.id!); else x.add(l.id!); return x })}><Chev />{l.label}<span className="fx-term">{l.term}</span></button>
          : <span>{l.label}{l.term && <span className="fx-term">{l.term}</span>}</span>
        return (
          <div key={i}>
            <div className={'fx-tr' + cls} style={style}>
              <div>{label}{l.note && <span className="fx-note">{l.note}</span>}</div>
              {(l.values ?? []).map((v, j) => <span key={j} className="r">{cell(v, l.kind, l.kind === 'net')}</span>)}
              <span className="r fx-tcol">{cell(l.total ?? null, l.kind, true)}</span>
            </div>
            {l.kids && (
              <div className={'fx-kids' + (isOpen ? ' is-open' : '')}><div>
                {l.kids.map((k, j) => (
                  <div key={j} className="fx-tr fx-kid" style={style}>
                    <span>{k.label}</span>
                    {k.values.map((v, x) => <span key={x} className="r">{typeof v === 'number' && Math.abs(v) >= 0.005 ? m(v) : <span className="fx-dash">—</span>}</span>)}
                    <span className="r fx-tcol">{typeof k.total === 'number' ? m(k.total) : ''}</span>
                  </div>
                ))}
              </div></div>
            )}
          </div>
        )
      })}
    </div></div>
  )
}

/** Statement lines as CSV rows: one column per head plus the total. */
export function statementCsv(heads: string[], total: string, lines: StLine[]): (string | number)[][] {
  const out: (string | number)[][] = [['Line', 'Accounting name', ...heads, total, 'Note']]
  const v = (c: Cell) => c == null ? '' : typeof c === 'number' ? Math.round(c * 100) / 100 : c
  for (const l of lines) {
    if (l.kind === 'sec') { out.push([l.label.toUpperCase()]); continue }
    if (l.kind === 'note') continue
    out.push([l.label, l.term ?? '', ...(l.values ?? []).map(v), v(l.total ?? null), typeof l.note === 'string' ? l.note : ''])
    for (const k of l.kids ?? []) out.push(['  ' + k.label, '', ...k.values.map(v), v(k.total), ''])
  }
  return out
}

export function downloadRows(name: string, rows: (string | number | null | undefined)[][]) {
  const esc = (x: unknown) => { const s = x == null ? '' : String(x); return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  const csv = '﻿' + rows.map((r) => r.map(esc).join(',')).join('\r\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

/** Export CSV, with a short menu of files. */
export function ExportMenu({ items }: { items: { label: string; sub: string; run: () => unknown | Promise<unknown> }[] }) {
  const [open, setOpen] = useState(false)
  const [msg, setMsg] = useState('')
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])
  useEffect(() => { if (!msg) return; const t = setTimeout(() => setMsg(''), 3000); return () => clearTimeout(t) }, [msg])
  return (
    <div className="fx-menu" ref={box}>
      <button type="button" className="rp-btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{msg || 'Export CSV ▾'}</button>
      {open && (
        <div className="fx-menu__list" role="menu">
          {items.map((it) => (
            <button key={it.label} type="button" role="menuitem" onClick={async () => { setOpen(false); try { await it.run(); setMsg('Downloaded') } catch (e) { setMsg((e as Error).message) } }}>
              {it.label}<small>{it.sub}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ---- column chart ------------------------------------------------------------------------------------------------------------
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const dayLabel = (d: string) => `${DOW[new Date(d + 'T12:00:00Z').getUTCDay()]} ${Number(d.slice(8, 10))}`
function niceMax(top: number) {
  if (top <= 0) return 4
  const base = Math.pow(10, Math.floor(Math.log10(top / 4)))
  const step = [1, 1.5, 2, 2.5, 3, 5, 10].map((k) => k * base).find((s) => s * 4 >= top) ?? base * 10
  return step * 4
}
const kLabel = (v: number) => v >= 1000 ? '$' + (v / 1000).toFixed(v % 1000 ? 1 : 0).replace('.0', '') + 'k' : '$' + Math.round(v)

/** Columns by day: one black column (and, with `b`, a light grey one beside it), each with its amount above. */
export function DayColumns({ days, aLabel, bLabel, ariaLabel, tip }: { days: { day: string; a: number; b?: number }[]; aLabel: string; bLabel?: string; ariaLabel: string; tip?: (i: number) => string }) {
  const two = days.some((d) => d.b != null)
  const top = Math.max(1, ...days.map((d) => Math.max(d.a, d.b ?? 0)))
  const max = niceMax(top), step = max / 4
  const W = 1000, H = 300, L = 58, R = 16, T = 26, B = 40
  const cw = (W - L - R) / Math.max(1, days.length)
  const bw = Math.min(38, (cw - 16) / (two ? 2 : 1)), gap = two ? 6 : 0
  const y = (v: number) => T + (1 - v / max) * (H - T - B)
  const small = days.length > 16
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
      {[0, 1, 2, 3, 4].map((i) => <g key={i}><line x1={L} x2={W - R} y1={y(step * i)} y2={y(step * i)} stroke={i ? '#ececf0' : '#d8d8dc'} /><text x={L - 10} y={y(step * i) + 4} textAnchor="end" fontSize="11" fill="#6c6c70">{kLabel(step * i)}</text></g>)}
      {days.map((d, k) => {
        const cx = L + cw * k + cw / 2
        const xa = two ? cx - gap / 2 - bw : cx - bw / 2, xb = cx + gap / 2
        const hasA = d.a > 0.004, hasB = (d.b ?? 0) > 0.004
        return (
          <g key={d.day}>
            {hasA && <rect className="fx-col" style={{ ['--d' as string]: `${200 + k * 50}ms` }} x={xa} y={y(d.a)} width={bw} height={Math.max(2, y(0) - y(d.a))} rx="3" fill="#0b0b0c"><title>{`${dayLabel(d.day)}: ${aLabel.toLowerCase()} ${m(d.a)}${tip ? ' (' + tip(k) + ')' : ''}`}</title></rect>}
            {hasA && !small && <text className="fx-lab" style={{ ['--d' as string]: `${600 + k * 50}ms` }} x={xa + bw / 2} y={y(d.a) - 7} textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#0b0b0c">{m0(d.a)}</text>}
            {hasB && <rect className="fx-col" style={{ ['--d' as string]: `${260 + k * 50}ms` }} x={xb} y={y(d.b!)} width={bw} height={Math.max(2, y(0) - y(d.b!))} rx="3" fill="#c7c7cc"><title>{`${dayLabel(d.day)}: ${(bLabel ?? '').toLowerCase()} ${m(d.b)}`}</title></rect>}
            {hasB && !small && <text className="fx-lab" style={{ ['--d' as string]: `${660 + k * 50}ms` }} x={xb + bw / 2} y={y(d.b!) - 7} textAnchor="middle" fontSize="11.5" fill="#6c6c70">−{m0(d.b)}</text>}
            {!hasA && !hasB && <text x={cx} y={y(0) - 7} textAnchor="middle" fontSize="11.5" fill="#aeaeb2">—</text>}
            {(!small || k % 2 === 0) && <text x={cx} y={H - 20} textAnchor="middle" fontSize={small ? 10.5 : 12} fontWeight="600" fill="#3a3a3c">{small ? Number(d.day.slice(8, 10)) : dayLabel(d.day)}</text>}
          </g>
        )
      })}
    </svg>
  )
}

/** A bar split into the buckets, each part growing from the left. */
export function BucketBar({ values, used }: { values: Partial<BMap>; used: Bucket[] }) {
  const total = used.reduce((a, k) => a + Math.max(0, n(values[k])), 0) || 1
  return (
    <div className="fx-bbar" role="img" aria-label={used.map((k) => `${BUCKET_LABEL[k]} ${Math.round(100 * Math.max(0, n(values[k])) / total)}%`).join(', ')}>
      {used.filter((k) => n(values[k]) > 0).map((k, i) => <i key={k} style={{ width: `${100 * n(values[k]) / total}%`, background: BUCKET_COLOR[k], ['--d' as string]: `${200 + i * 120}ms` }} />)}
    </div>
  )
}
